import { useCallback, useState } from "react";

import { useLibrary } from "../../../../providers";
import { useRequestWithFeedback } from "../../../../composites";
import { useObjectMemo } from "../../../../hooks";

import { customAlertAPI } from "./api";

import type { NetworkPayload, OverrideRunProps } from "../../../../composites";
import type { CustomAlertPayload } from "../../../modules/alerts/custom";

export const useGetCustomAlerts = () => {
  const [customAlerts, setCustomAlerts] = useState<API.UserSavedSearch[]>([]);
  const { send, loading } =
    useRequestWithFeedback<
      API.Paginated<"saved_searches", API.UserSavedSearch>
    >();

  const fetchAdvancedAlerts = useCallback(
    (
      overrideRunProps: OverrideRunProps<
        API.Paginated<"saved_searches", API.UserSavedSearch>
      > = {},
    ) => {
      const { onData, ...restRunProps } = overrideRunProps;
      return send({
        action: customAlertAPI.getAlerts(),
        onData(data) {
          setCustomAlerts(data.saved_searches || []);
          onData?.(data);
        },
        ...restRunProps,
      });
    },
    [send],
  );

  return useObjectMemo({
    send: fetchAdvancedAlerts,
    loading,
    customAlerts,
  });
};

export const useCreateCustomAlert = () => {
  const t = useLibrary("translations");
  const { send, loading } = useRequestWithFeedback<API.UserSavedSearch>();

  const createCustomAlert = useCallback(
    (
      payload: CustomAlertPayload,
      overrideRunProps: OverrideRunProps<API.UserSavedSearch> = {},
    ) => {
      return send({
        action: customAlertAPI.saveAlert(payload) as unknown as NetworkPayload,
        messaging: {
          toast: {
            success: t
              ? t.getString("alerts-toast-success-create", {}, "Alert saved.")
              : "Alert saved.",
            error: t
              ? t.getString(
                  "alerts-toast-error-create",
                  {},
                  "Could not delete alert.",
                )
              : "Could not delete alert.",
          },
        },
        ...overrideRunProps,
      });
    },
    [send, t],
  );

  return useObjectMemo({
    loading,
    send: createCustomAlert,
  });
};

export const useDeleteCustomAlert = () => {
  const t = useLibrary("translations");
  const { send, loading } = useRequestWithFeedback<null>();

  const deleteCustomAlert = useCallback(
    (
      customAlertId: API.SavedSearchId,
      overrideRunProps: OverrideRunProps<null> = {},
    ) => {
      return send({
        action: customAlertAPI.deleteAlert(customAlertId),
        messaging: {
          toast: {
            success: t
              ? t.getString("alerts-toast-success-delete", {}, "Alert deleted.")
              : "Alert deleted.",
            error: t
              ? t.getString(
                  "alerts-toast-error-delete",
                  {},
                  "Could not delete alert.",
                )
              : "Could not delete alert.",
          },
        },
        ...overrideRunProps,
      });
    },
    [t, send],
  );

  return useObjectMemo({
    loading,
    send: deleteCustomAlert,
  });
};
